import { useRef } from 'react';
import { FormControl, FormLabel, Select, Text, HStack, Box } from '@chakra-ui/react';
import { Field, useFormikContext } from 'formik';
import { GoChevronDown } from 'react-icons/go';
import styled from '@emotion/styled';

import FormErrorMessage from './FormErrorMessage';
import FormMediaField from './FormMediaField';
import { formFields } from '../consts/formConsts';
import { useWhatsAppTemplates } from '../../context/WhatsAppTemplatesContext';
import { UnregisteredEmployeeFields, isMediaHeaderType } from '../consts/consts';

const FormWhatsappTemplateSelection = ({ messageIndex }) => {
  const { whatsappTemplates, isWhatsappTemplatesLoading, getParametersFromText } =
    useWhatsAppTemplates();

  const { setFieldValue, values, touched } = useFormikContext();

  const previousTemplateRef = useRef(null);

  return (
    <FormControl mt={3}>
      <FormLabel fontSize="sm">{formFields.messageSequence.messages.templateKey.label}</FormLabel>
      <Field name={`messageSequence.messages.${messageIndex}.templateKey`}>
        {({ field }) => {
          const template = whatsappTemplates.find((t) => t.name === field.value) || null;
          const parameters =
            (template && template?.preview && getParametersFromText(template.preview)) || [];

          // Check if template has changed and needs params reset
          if (previousTemplateRef.current !== null && previousTemplateRef.current !== field.value) {
            if (parameters.length > 0) {
              setFieldValue(
                `messageSequence.messages.${messageIndex}.params`,
                new Array(parameters.length).fill(''),
              );
            } else {
              setFieldValue(`messageSequence.messages.${messageIndex}.params`, []);
            }
          }

          // Update ref for next render
          previousTemplateRef.current = field.value;

          return (
            <>
              <StyledSelect
                {...field}
                placeholder={
                  isWhatsappTemplatesLoading
                    ? 'טוען תבניות...'
                    : formFields.messageSequence.messages.templateKey.placeholder
                }
                isDisabled={isWhatsappTemplatesLoading}
                color={field.value ? 'inherit' : 'gray.400'}
                iconColor="black"
                icon={<GoChevronDown />}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const oldValue = field.value;

                  field.onChange(e);

                  // If template has changed, reset params immediately
                  if (oldValue !== newValue) {
                    const newTemplate = whatsappTemplates.find((t) => t.name === newValue) || null;
                    const newParameters = newTemplate?.preview
                      ? getParametersFromText(newTemplate.preview)
                      : [];

                    if (newParameters.length > 0) {
                      setFieldValue(
                        `messageSequence.messages.${messageIndex}.params`,
                        new Array(newParameters.length).fill(''),
                      );
                    } else {
                      setFieldValue(`messageSequence.messages.${messageIndex}.params`, []);
                    }

                    // A template without a media header must be sent with no mediaUrl at all,
                    // so drop whatever the previous template carried
                    if (!isMediaHeaderType(newTemplate?.headerType)) {
                      setFieldValue(`messageSequence.messages.${messageIndex}.mediaUrl`, '');
                    }
                  }
                }}
              >
                {whatsappTemplates.map((template) => (
                  <option key={template.id} value={template.name}>
                    {template.name}
                  </option>
                ))}
              </StyledSelect>

              {template && (
                <>
                  <Text w="90%" mt={2}>
                    {template?.preview || ''}
                  </Text>

                  {isMediaHeaderType(template.headerType) && (
                    <FormMediaField messageIndex={messageIndex} headerType={template.headerType} />
                  )}

                  {parameters.length > 0 && (
                    <Box mt={4}>
                      <FormLabel fontSize="sm">
                        {formFields.messageSequence.messages.params.label}
                      </FormLabel>
                      {parameters.map((param, index) => {
                        const fieldName = `messageSequence.messages.${messageIndex}.params[${index}]`;
                        // Check if this specific parameter has an error
                        const hasError =
                          touched.messageSequence?.messages?.[messageIndex]?.params?.[index] &&
                          !values.messageSequence.messages[messageIndex].params?.[index];

                        return (
                          <HStack key={index} mt={2} alignItems="flex-start">
                            <Text minWidth="24px" fontWeight="bold" mt="10px">
                              {param} :
                            </Text>
                            <Field name={fieldName}>
                              {({ field: paramField }) => (
                                <FormControl>
                                  <StyledSelect
                                    {...paramField}
                                    placeholder={
                                      formFields.messageSequence.messages.params.placeholder
                                    }
                                    color={paramField.value ? 'inherit' : 'gray.400'}
                                    iconColor="black"
                                    icon={<GoChevronDown />}
                                  >
                                    {Object.entries(UnregisteredEmployeeFields).map(
                                      ([key, label]) => (
                                        <option key={key} value={key}>
                                          {label}
                                        </option>
                                      ),
                                    )}
                                  </StyledSelect>
                                  {hasError && (
                                    <Box minH="20px">
                                      <ErrorMessageText>יש לבחור שדה לפרמטר</ErrorMessageText>
                                    </Box>
                                  )}
                                </FormControl>
                              )}
                            </Field>
                          </HStack>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </>
          );
        }}
      </Field>
      <FormErrorMessage name={`messageSequence.messages.${messageIndex}.templateKey`} />
    </FormControl>
  );
};

export default FormWhatsappTemplateSelection;

// ----------------------------------- Styled Components -----------------------------------

const StyledSelect = styled(Select)`
  padding-inline: 32px 16px;
`;

const ErrorMessageText = styled.div`
  font-size: 12px;
  color: red;
  margin-right: 2px;
`;
